import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import SmoothScroll from "./components/SmoothScroll";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import FloatingOrderOrb from "./components/FloatingOrderOrb";
import Home from "./pages/Home";
import MenuPage from "./pages/MenuPage";
import AboutPage from "./pages/AboutPage";
import BookPage from "./pages/BookPage";
import VouchersPage from "./pages/VouchersPage";
import VouchersSuccessPage from "./pages/VouchersSuccessPage";
import VouchersBalancePage from "./pages/VouchersBalancePage";
import AdminPage from "./pages/AdminPage";
import { trackEvent } from "./lib/api";

function PublicShell({ children }) {
    return (
        <SmoothScroll>
            <div className="App relative min-h-screen bg-[#FAF8F4] text-[#121212]">
                <ScrollToTop />
                <Navigation />
                <main data-testid="page-main">{children}</main>
                <Footer />
                <FloatingOrderOrb />
            </div>
        </SmoothScroll>
    );
}

function RouterContent() {
    const { pathname } = useLocation();

    // Page-view analytics. `trackEvent` self-skips the /admin route.
    useEffect(() => {
        trackEvent("page_view", pathname);
    }, [pathname]);

    // CTA click delegate — single global listener so we don't have to
    // instrument every <a> / <Link> in the codebase. We classify the click
    // based on the resolved href; the trackEvent helper itself skips /admin.
    useEffect(() => {
        function classify(href) {
            if (!href) return null;
            const h = href.toLowerCase();
            if (h.includes("chewzie.me")) return "order_pickup";
            if (h.includes("ubereats.com")) return "delivery";
            if (h.startsWith("tel:")) return "phone_call";
            if (h.includes("google.com/maps")) return "directions";
            if (h.includes("facebook.com")) return "facebook";
            // Internal SPA routes — react-router rewrites <Link> to
            // <a href="/book"> etc., so we can match by pathname suffix.
            try {
                const u = new URL(href, window.location.origin);
                if (u.origin === window.location.origin) {
                    if (u.pathname === "/book") return "book_table";
                    if (u.pathname === "/menu") return "see_menu";
                }
            } catch {
                /* not a parseable URL */
            }
            return null;
        }
        function onClick(e) {
            const a = e.target.closest("a");
            if (!a) return;
            const target = classify(a.getAttribute("href"));
            if (target) trackEvent("cta_click", target);
        }
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    // The admin dashboard owns its own chrome (dark theme, no nav/footer)
    if (pathname.startsWith("/admin")) {
        return (
            <Routes>
                <Route path="/admin" element={<AdminPage />} />
            </Routes>
        );
    }
    return (
        <PublicShell>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/menu" element={<MenuPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/book" element={<BookPage />} />
                <Route path="/vouchers" element={<VouchersPage />} />
                <Route
                    path="/vouchers/balance"
                    element={<VouchersBalancePage />}
                />
                <Route
                    path="/vouchers/success"
                    element={<VouchersSuccessPage />}
                />
            </Routes>
        </PublicShell>
    );
}

function App() {
    return (
        <BrowserRouter>
            <RouterContent />
        </BrowserRouter>
    );
}

export default App;
